import React, { useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useAuth } from './AuthContext';
import ChangePassword from './ChangePassword';
import './Portal.css';

/**
 * Chrome shared by both portals: header, sign-out, tab strip and the account
 * panel. Tabs are local state rather than routes — a portal tab is not
 * something anyone needs to bookmark or share.
 */
export default function PortalShell({ roleLabel, tabs, activeTab, onTabChange, title, subtitle, children }) {
  const { user, signOut } = useAuth();
  const history = useHistory();
  const [signingOut, setSigningOut] = useState(false);

  const handleSignOut = async () => {
    setSigningOut(true);
    await signOut();
    history.push('/portal');
  };

  return (
    <div className="pt-app">
      <header className="pt-topbar">
        <div className="pt-topbar-inner">
          <a className="pt-brand" href="/">
            <span className="pt-brand-name">Northwoods Handy</span>
            <span className="pt-brand-role">{roleLabel}</span>
          </a>

          <div className="pt-topbar-spacer" />

          <div className="pt-whoami">
            <strong>{user ? user.name : ''}</strong>
            {user ? user.email : ''}
          </div>

          <button className="pt-btn pt-btn-ghost pt-btn-sm" onClick={handleSignOut} disabled={signingOut}>
            {signingOut ? 'Signing out…' : 'Sign out'}
          </button>
        </div>

        <nav className="pt-tabs">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              className={'pt-tab' + (activeTab === tab.id ? ' is-active' : '')}
              onClick={() => onTabChange(tab.id)}
              type="button"
            >
              {tab.label}
            </button>
          ))}
          <button
            className={'pt-tab' + (activeTab === 'account' ? ' is-active' : '')}
            onClick={() => onTabChange('account')}
            type="button"
          >
            Account
          </button>
        </nav>
      </header>

      <main className="pt-main">
        {user && user.password_is_temporary && activeTab !== 'account' && (
          <div className="pt-notice pt-notice-warn" style={{ marginBottom: 22 }}>
            You are still signed in with the password you were issued. Please{' '}
            <button
              type="button"
              onClick={() => onTabChange('account')}
              style={{
                background: 'none',
                border: 0,
                padding: 0,
                font: 'inherit',
                fontWeight: 800,
                color: 'inherit',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              set your own password
            </button>{' '}
            — it was sent to you in plain text, so treat it as temporary.
          </div>
        )}

        {activeTab === 'account' ? (
          <>
            <h1 className="pt-page-title">Your account</h1>
            <p className="pt-page-sub">Change the password you use to sign in to this portal.</p>
            <ChangePassword />
          </>
        ) : (
          <>
            {title && <h1 className="pt-page-title">{title}</h1>}
            {subtitle && <p className="pt-page-sub">{subtitle}</p>}
            {children}
          </>
        )}
      </main>
    </div>
  );
}
